import Vue from 'vue';
import { ActionContext, Commit } from 'vuex';

import { Rule, RuleAddEdit, RuleTemplate } from '@/interfaces/rules';

interface RuleState {
	templates: {
		[id: number]: RuleTemplate;
	};
	rules: {
		[id: number]: Rule;
	};
	internalRules: {
		[id: number]: Rule;
	};
}

export default {
	namespaced: true,
	state: {
		templates: {},
		rules: {},
		internalRules: {},
	} as RuleState,
	getters: {
		getRules: (state: RuleState): Rule[] => {
			return Object.values(state.rules);
		},

		getRulesCertainType:
			(state: RuleState) =>
			(type: string): Rule[] => {
				return Object.values(state.rules).filter((rule: Rule) => rule.fk_rule_type === type);
			},

		getRuleByGroup:
			(state: RuleState) =>
			(id: number): Rule | undefined => {
				return Object.values(state.rules).find((rule: Rule) => rule?.fk_group === id);
			},

		getInternalRules: (state: RuleState): Rule[] => {
			return Object.values(state.internalRules);
		},

		getInternalRulesCertainType:
			(state: RuleState) =>
			(type: string): Rule[] => {
				return Object.values(state.internalRules).filter(
					(rule: Rule) => rule.fk_rule_type === type
				);
			},

		getRule:
			(state: RuleState) =>
			(id: number): Rule | undefined => {
				return state.rules[id];
			},

		getRuleTemplates: (state: RuleState): RuleTemplate[] => {
			return Object.values(state.templates);
		},

		getRuleTemplatesByType:
			(state: RuleState) =>
			(type: string): RuleTemplate[] => {
				return Object.values(state.templates).filter((template) => template.fk_rule_type === type);
			},

		getRuleTemplate:
			(state: RuleState) =>
			(id: number): RuleTemplate | undefined => {
				return state.templates[id];
			},
	},
	mutations: {
		SET_TEMPLATES(state: RuleState, rules: RuleTemplate[]): void {
			const ret: { [id: number]: RuleTemplate } = {};
			const templates = rules.reduce((obj, tpl) => {
				obj[tpl.id] = tpl;
				return obj;
			}, ret);
			state.templates = {
				...state.templates,
				...templates,
			};
		},
		SET_RULES(state: RuleState, rules: Rule[]): void {
			const ret: { [id: number]: Rule } = {};
			state.rules = rules.reduce((obj, rule) => {
				obj[rule.id] = rule;
				return obj;
			}, ret);
		},
		SET_INTERNAL_RULES(state: RuleState, rules: Rule[]): void {
			const ret: { [id: number]: Rule } = {};
			state.internalRules = rules.reduce((obj, rule) => {
				obj[rule.id] = rule;
				return obj;
			}, ret);
		},

		ADD_TEMPLATE(state: RuleState, tpl: RuleTemplate): void {
			state.templates = {
				...state.templates,
				[tpl.id]: tpl,
			};
		},

		CHANGE_TEMPLATE(state: RuleState, tpl: Partial<RuleTemplate> & { id: number }): void {
			const oldTemplate = state.templates[tpl.id] || {};
			state.templates = {
				...state.templates,
				[tpl.id]: { ...oldTemplate, ...tpl },
			};
		},

		REMOVE_TEMPLATE(state: RuleState, id: number): void {
			Vue.delete(state.templates, id);
		},

		ADD_RULE(state: RuleState, rule: Rule): void {
			Vue.set(state.rules, rule.id, rule);
		},

		REMOVE_RULE(state: RuleState, id: number): void {
			Vue.delete(state.rules, id);
		},

		CHANGE_RULE(state: RuleState, rule: Rule & { id: number }): void {
			const oldRule = state.rules[rule.id] || {};
			state.rules = {
				...state.rules,
				[rule.id]: { ...oldRule, ...rule },
			};
		},

		CLEAN_TEMPLATES(state: RuleState): void {
			state.templates = {};
		},
		CLEAN_RULES(state: RuleState): void {
			state.rules = {};
		},
		CLEAN_INTERNAL_RULES(state: RuleState): void {
			state.internalRules = {};
		},
	},
	actions: {
		async fetchRuleTemplates({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/rules/templates`);

			if (response && response.status === 200) {
				commit('SET_TEMPLATES', response.data.data);
			}
		},
		async fetchRules({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/rules`);

			if (response && response.status === 200) {
				commit('SET_RULES', response.data.data);
			}
		},

		async fetchInternalRules({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/rules/internal`);

			if (response && response.status === 200) {
				commit('SET_INTERNAL_RULES', response.data.data);
			}
		},

		async addRule({ commit }: { commit: Commit }, { rule }: { rule: RuleAddEdit }): Promise<void> {
			const response = await Vue.axios.post(`${process.env.VUE_APP_API_URL}/rules`, rule);
			if (response && [200, 201].includes(response.status)) {
				commit('ADD_RULE', response.data.data);
			}
		},

		async addTemplate(
			{ commit }: { commit: Commit },
			{ template }: { template: RuleAddEdit }
		): Promise<void> {
			const response = await Vue.axios.post(
				`${process.env.VUE_APP_API_URL}/rules/templates`,
				template
			);
			if (response && response.status == 201) {
				commit('ADD_TEMPLATE', response.data.data);
			}
		},

		async modifyTemplate(
			{ commit }: { commit: Commit },
			{ id, template }: { id: number; template: RuleAddEdit }
		): Promise<void> {
			const response = await Vue.axios.put(
				`${process.env.VUE_APP_API_URL}/rules/templates/${id}`,
				template
			);
			if (response && response.status == 200) {
				commit('CHANGE_TEMPLATE', response.data.data);
			}
		},

		async deleteTemplateRule(
			{ commit }: { commit: Commit },
			{ id }: { id: number }
		): Promise<void> {
			const response = await Vue.axios.delete(
				`${process.env.VUE_APP_API_URL}/rules/templates/${id}`
			);
			if (response && response.status === 200) {
				commit('REMOVE_TEMPLATE', id);
			}
		},

		async createRuleFromTemplate(
			{ commit }: { commit: Commit },
			{ id, name, sensors }: { id: number; name: string; sensors: string[] }
		): Promise<void> {
			const response = await Vue.axios.post(
				`${process.env.VUE_APP_API_URL}/rules/templates/${id}/create`,
				{
					name,
					parameters: { sensors },
				}
			);
			if (response && response.status == 200) {
				commit('ADD_RULE', response.data.data);
			}
		},

		async applyFromTemplate(
			context: ActionContext<RuleState, unknown>,
			{
				templateId,
				sensors,
				movementSensors,
				lightingSensor,
				zoneId,
			}: {
				templateId: number;
				sensors: string[];
				movementSensors: string[];
				lightingSensor: string;
				zoneId: number;
			}
		): Promise<void> {
			const template: RuleTemplate = context.getters.getRuleTemplate(templateId);
			const response = await Vue.axios.post(
				`${process.env.VUE_APP_API_URL}/rules/templates/${templateId}/create`,
				{
					name: template.name,
					parameters: { sensors, movementSensors, lightingSensor },
				}
			);
			if (response && response.status === 200) {
				const existingApplied: Rule = context.getters['getRuleByGroup'](zoneId);
				const rule: Rule = response.data.data;
				await context.dispatch(
					'activities/appliedRules/addAppliedRuleGroup',
					{
						params: {
							fk_group: zoneId,
							fk_rule: rule.id,
						},
					},
					{ root: true }
				);
				if (typeof existingApplied !== 'undefined') {
					context.commit('REMOVE_RULE', existingApplied.id);
				}
			}
		},
		cleanRuleTemplates({ commit }: { commit: Commit }): void {
			commit('CLEAN_TEMPLATES');
		},
		cleanRule({ commit }: { commit: Commit }): void {
			commit('CLEAN_RULES');
		},
		cleanInternalRule({ commit }: { commit: Commit }): void {
			commit('CLEAN_INTERNAL_RULES');
		},
	},
};
